import { inject } from 'inversify';

import { ZoneManager } from './ZoneManager';

import { REQUEST_STATUS, PROVIDER_STATUS, ZONE_TYPE_VALUE } from '@/constants/index';
import { UserController } from '@/controllers/UserController';
import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { TransportRequest } from '@/entities/TransportRequest';
import CONSTANTS from '@/helpers/constants';
import { AttachmentManager } from '@/managers/AttachmentManager';
import { Connection } from '@/services/Connection';
import { TransportRequestUpdateReqMsg } from '@/services/signalr/MessageTypes/TransportRequest';
import { Signalr } from '@/services/signalr/Signalr';
import { TransportRequestStore } from '@/stores/TransportRequestStore';
import { getFormChanges } from '@/utils/deepComparision';
import { messageOrderComparision } from '@/utils/messageOrderComparision';
import { normalize } from '@/utils/normalize';
import { ILocation } from '~/api/ILocation';
import { ITransportRequest } from '~/api/ITransportRequest';
import { IDispatchMessage } from '~/api/messages/IDispatchMessage';
import { ITransportRequestsRespMsg } from '~/api/messages/ITransportRequestRespMsg';

@SignalrListener
@service(TransportRequestManager)
export class TransportRequestManager {
  @inject(Signalr) protected signalr: Signalr;
  @inject(TransportRequestStore) protected transportRequestStore: TransportRequestStore;
  @inject(Connection) connection: Connection;
  @inject(UserController) protected userController: UserController;
  @inject(ZoneManager) protected zoneManager: ZoneManager;
  @inject(AttachmentManager) protected attachmentManager: AttachmentManager;

  @SignalrMessage('TransportRequestsRespMsg')
  async onTransportRequestsRespMsg(response: ITransportRequestsRespMsg & IDispatchMessage) {
    const validRequests: TransportRequest[] = [];

    response.transportRequests?.forEach((request) => {
      const normalizedRequest = normalize(request);
      const existingRequest = this.transportRequestStore.find(normalizedRequest.requestId);
      const sentTime = messageOrderComparision(response.sentTime, existingRequest?.sentTime);
      if (sentTime && Number(normalizedRequest.requestId) > 0) {
        validRequests.push({ ...normalizedRequest, sentTime: response.sentTime } as TransportRequest);

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (normalizedRequest.attachments && normalizedRequest.attachments.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          this.attachmentManager.populateExistingAttachments(normalizedRequest.attachments);
        }
      }
    });

    if (validRequests.length > 0) {
      this.transportRequestStore.batchUpdate(validRequests);
    }
  }

  async onTransportRequestUpdateReqMsg(transportRequest: ITransportRequest, history: ITransportRequest | null) {
    let requestData: ITransportRequest | Partial<ITransportRequest | null> | undefined;
    if (Number(transportRequest.requestId) > 0) {
      requestData = getFormChanges(transportRequest, history, true);
      if (requestData) {
        requestData.requestId = transportRequest.requestId;
        if (requestData.pickupLocation !== undefined) {
          requestData.pickupLocation.position = transportRequest.pickupLocation.position;
        }

        if (requestData.patient) {
          requestData.patient.id = transportRequest.patient?.id;
        }
      }
    }
    else {
      requestData = transportRequest;
    }

    if (requestData && this.connection.isPortal) {
      requestData.providerId = this.connection.networkedTenant.ownerId ?? null;
      requestData.tenantKey = this.userController.activeUser.tenantKey;
      requestData.tenantName = this.userController.activeUser.tenantName;
    }
    else if (requestData) {
      requestData.providerId = transportRequest.providerId;
      requestData.tenantKey = transportRequest.tenantKey;
      requestData.tenantName = transportRequest.tenantName;
    }

    const response = await this.signalr.invoke(TransportRequestUpdateReqMsg, {
      transportRequest: requestData, contextKey: CONSTANTS.UI.CONTEXTS.TRANSPORT_REQUEST,
    });

    return response;
  }

  async cancelPortalTransportRequest(request: ITransportRequest) {
    const transportRequest = {
      providerId: this.connection.networkedTenant.ownerId ?? null,
      tenantKey: this.userController.activeUser.tenantKey,
      tenantName: this.userController.activeUser.tenantName,
      requestId: request.requestId,
      requestStatus: REQUEST_STATUS.CANCEL.toString(),
      cancelReasonId: request.cancelReasonId,
      cancelComment: request.cancelComment,
      cancelWho: this.userController.activeUser.fullName,
    };

    const response = await this.signalr.invoke(TransportRequestUpdateReqMsg, {
      transportRequest, contextKey: CONSTANTS.UI.CONTEXTS.TRANSPORT_REQUEST_CANCEL,
    });

    return response;
  }

  async acknowledgePortalTransportRequest(transportRequestId: string) {
    const transportRequest = {
      providerId: this.connection.networkedTenant.ownerId ?? null,
      tenantKey: this.userController.activeUser.tenantKey,
      tenantName: this.userController.activeUser.tenantName,
      requestId: transportRequestId,
      requestStatus: REQUEST_STATUS.CANCEL_ACCEPT.toString(),
    };

    const response = await this.signalr.invoke(TransportRequestUpdateReqMsg, {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      transportRequest, contextKey: CONSTANTS.UI.CONTEXTS.TRANSPORT_REQUEST_CANCEL_ACCEPT,
    });

    return response;
  }

  async cancelDispatchTransportRequest(request: ITransportRequest) {
    const transportRequest = {
      providerId: request.providerId,
      tenantKey: request.tenantKey,
      tenantName: request.tenantName,
      requestId: request.requestId,
      providerStatus: PROVIDER_STATUS.CANCEL.toString(),
      cancelReasonId: request.cancelReasonId,
      cancelComment: request.cancelComment,
      cancelWho: this.userController.activeUser.fullName,
    };

    const response = await this.signalr.invoke(TransportRequestUpdateReqMsg, {
      transportRequest, contextKey: CONSTANTS.UI.CONTEXTS.TRANSPORT_REQUEST_CANCEL,
    });

    return response;
  }

  async acknowledgeDispatchTransportRequest(request: ITransportRequest) {
    const transportRequest = {
      providerId: request.providerId,
      tenantKey: request.tenantKey,
      tenantName: request.tenantName,
      requestId: request.requestId,
      providerStatus: PROVIDER_STATUS.CANCEL_ACCEPT.toString(),
    };

    const response = await this.signalr.invoke(TransportRequestUpdateReqMsg, {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      transportRequest, contextKey: CONSTANTS.UI.CONTEXTS.TRANSPORT_REQUEST_CANCEL_ACCEPT,
    });

    return response;
  }

  // Helper to assign zones to a location
  assignZones(location: ILocation) {
    const dispatchZone = this.zoneManager.getDispatchZone(location.position ?? { lat: 0, lng: 0 });
    const responseZone = this.zoneManager.getResponseZone(location.position ?? { lat: 0, lng: 0 });

    const updatedTransportLocation: ILocation = {
      position: location.position,
      zones: [],
      facilityId: null,
      facilityName: null,
      address: null,
      address2: null,
      city: null,
      region: null,
      postalCode: null,
      phone: null,
    };

    if (dispatchZone) {
      updatedTransportLocation.zones?.push({
        type: ZONE_TYPE_VALUE.DISPATCH,
        code: dispatchZone.properties.code.split('_')[1],
        name: dispatchZone.properties.name,
      });
    }
    if (responseZone) {
      updatedTransportLocation.zones?.push({
        type: ZONE_TYPE_VALUE.RESPONSE,
        code: responseZone.properties.code.split('_')[1],
        name: responseZone.properties.name,
      });
    }
    return updatedTransportLocation;
  }

  async acceptDispatchTransportRequest(request: ITransportRequest) {
    const transportRequest: Partial<ITransportRequest> = {
      providerId: request.providerId,
      tenantKey: request.tenantKey,
      tenantName: request.tenantName,
      requestId: request.requestId,
      providerStatus: PROVIDER_STATUS.APPROVED.toString(),
    };

    if (request.pickupLocation?.position && request.pickupLocation.zones?.length === 0) {
      transportRequest.pickupLocation = this.assignZones(request.pickupLocation);
    }
    if (request.dropoffLocation?.position && request.dropoffLocation.zones?.length === 0) {
      transportRequest.dropoffLocation = this.assignZones(request.dropoffLocation);
    }

    if (request.returnPickupTime) {
      transportRequest.returnPickupTime = request.returnPickupTime;
    }
    if (request.returnWillCall) {
      transportRequest.returnWillCall = request.returnWillCall;
    }

    const response = await this.signalr.invoke(TransportRequestUpdateReqMsg, {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      transportRequest, contextKey: CONSTANTS.UI.CONTEXTS.TRANSPORT_REQUEST_ACCEPT,
    });

    return response;
  }

  get getPendingRequests() {
    return this.transportRequestStore.all()
      .filter((request) => Number(request.requestStatus) < REQUEST_STATUS.CANCEL &&
    Number(request.providerStatus) <= PROVIDER_STATUS.CANCEL);
  }

  get getInboundQueueRequests() {
    return this.transportRequestStore.all()
      .filter((request) => Number(request.providerStatus) < PROVIDER_STATUS.CANCEL);
  }
}
